import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { cn } from "@/lib/utils";
import { Activity, Users, Bot, Workflow, AlertTriangle, TrendingUp, TrendingDown, Minus, Zap, ArrowRight, GitBranch, Repeat } from "lucide-react";
import { DOW, SOURCE_COLOR, SOURCE_LABEL, fmtTime, Stat, CoverageChip } from "./shared";
import { useAtlas } from "@/context/AtlasContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const PulseForbidden = () => (<div className="max-w-3xl mx-auto panel p-10 text-center text-sm text-slate-500" data-testid="pulse-forbidden">You don't have access to Pulse in this workspace.</div>);

const RANGES = [["Today", 1], ["7 days", 7], ["30 days", 30], ["90 days", 90]];
const GROUPS = [["Source", "source"], ["Human vs AI", "actor_type"], ["Module", "module"]];

export default function PulseOverview() {
  const { has } = useAtlas();
  const [days, setDays] = useState(7);
  const [sum, setSum] = useState(null);
  const [wall, setWall] = useState(null);
  const [stream, setStream] = useState([]);
  const [insights, setInsights] = useState([]);
  const [groupBy, setGroupBy] = useState("source");
  const [actorFilter, setActorFilter] = useState("");
  const [promoteObs, setPromoteObs] = useState(null);
  const [promoting, setPromoting] = useState(false);

  const promote = async (target) => {
    if (!promoteObs) return;
    setPromoting(true);
    try {
      const { data } = await api.post("/pulse/observations/promote", { observation: promoteObs, target });
      const msg = target === "decision"
        ? (data.state === "already" ? "Already open in your Decisions inbox"
           : data.state === "reopened" ? "Re-opened in the Decisions inbox — this observation recurred"
           : "Opened in the Decisions inbox for review")
        : (data.already ? "A standing order for this pattern already exists" : "Standing order created — it will re-check on schedule");
      toast.success(msg);
      setPromoteObs(null);
    } catch (e) { toast.error(apiError(e)); } finally { setPromoting(false); }
  };

  const load = useCallback(() => {
    api.get("/pulse/overview", { params: { days } }).then((r) => setSum(r.data)).catch(() => {});
    api.get("/pulse/insights", { params: { days } }).then((r) => setInsights(r.data)).catch(() => {});
  }, [days]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/pulse/wall", { params: { days, group_by: groupBy, actor_type: actorFilter || undefined } }).then((r) => setWall(r.data)).catch(() => {}); }, [days, groupBy, actorFilter]);
  useEffect(() => { api.get("/pulse/stream", { params: { limit: 30, actor_type: actorFilter || undefined } }).then((r) => setStream(r.data)).catch(() => {}); }, [actorFilter, days]);

  if (!has("pulse.view")) return <PulseForbidden />;
  return (
    <div className="max-w-6xl mx-auto space-y-5" data-testid="pulse-overview">
      <PageHeader title="Pulse" subtitle="The operational heartbeat — what is actually happening across Total Operations. Activity is evidence of work, not a productivity score.">
        <div className="flex items-center gap-2">
          {sum && <CoverageChip pct={sum.coverage_pct} testid="pulse-coverage" />}
          <div className="flex rounded-full border p-0.5 bg-white" style={{ borderColor: "var(--line)" }} data-testid="pulse-range">
            {RANGES.map(([l, d]) => <button key={d} onClick={() => setDays(d)} className={cn("px-3 py-1 rounded-full text-xs", days === d ? "bg-slate-900 text-white" : "text-slate-600")} data-testid={`range-${d}`}>{l}</button>)}
          </div>
        </div>
      </PageHeader>

      {sum && <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pulse-summary">
        <Stat label="Observed activity" value={sum.observed_activity?.toLocaleString()} sub={`${sum.human_events} human · ${sum.ai_events} machine`} testid="stat-activity" />
        <Stat label="Active people" value={sum.active_people} sub={sum.adoption_pct != null ? `${sum.adoption_pct}% adoption` : "—"} testid="stat-people" />
        <Stat label="Active AI agents" value={sum.active_agents} sub="Agents + automations" accent="#7c3aed" testid="stat-agents" />
        <Stat label="Workflows completed" value={sum.workflows_completed} sub={`${sum.blocked_work} blocked`} accent="#0f766e" testid="stat-workflows" />
      </div>}

      {/* Pulse Wall */}
      <div className="panel p-4" data-testid="pulse-wall">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-slate-400" /><p className="eyebrow flex-1">Pulse Wall · when the organization is active</p>
          <div className="flex rounded-full border p-0.5 bg-white text-xs" style={{ borderColor: "var(--line)" }}>
            {[["All", ""], ["Human", "human"], ["AI", "ai"]].map(([l, v]) => <button key={v} onClick={() => setActorFilter(v)} className={cn("px-2.5 py-1 rounded-full", actorFilter === v ? "bg-slate-900 text-white" : "text-slate-600")} data-testid={`wall-actor-${v || "all"}`}>{l}</button>)}
          </div>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="rounded-md border px-2 py-1 text-xs bg-white" style={{ borderColor: "var(--line)" }} data-testid="wall-groupby">
            {GROUPS.map(([l, v]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {wall && <WallGrid wall={wall} />}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Insights */}
        <div className="panel p-4" data-testid="pulse-insights">
          <p className="eyebrow mb-2">Insights · factual changes</p>
          {insights.length === 0 && <p className="text-sm text-slate-400">No significant changes detected in this period.</p>}
          <div className="space-y-2">{insights.map((o) => (
            <div key={o.key} className="flex items-start gap-2 rounded-lg border p-2.5 text-sm" style={{ borderColor: "var(--line)" }} data-testid="pulse-insight">
              <span className="mt-0.5">{o.direction === "up" ? <TrendingUp className="w-4 h-4 text-teal-600" /> : o.direction === "down" ? <TrendingDown className="w-4 h-4 text-rose-600" /> : <Minus className="w-4 h-4 text-slate-400" />}</span>
              <div className="flex-1"><p className="text-slate-800">{o.text}</p>{o.current != null && <p className="text-[11px] text-slate-400 mt-0.5">now {o.current}{o.previous != null ? ` · was ${o.previous}` : ""} · {o.kind}</p>}</div>
              <button onClick={() => setPromoteObs(o)} className="flex-shrink-0 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-900 hover:text-white transition-colors" style={{ borderColor: "var(--line)" }} data-testid={`pulse-promote-${o.key}`}>Turn into action <ArrowRight className="w-3 h-3" /></button>
            </div>))}</div>
        </div>

        <PromoteDialog obs={promoteObs} onClose={() => setPromoteObs(null)} onPromote={promote} busy={promoting} canStanding={has("admin.access")} />

        {/* Activity stream */}
        <div className="panel p-4" data-testid="pulse-stream">
          <p className="eyebrow mb-2">Activity stream</p>
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto">{stream.map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-sm py-1 border-b last:border-0" style={{ borderColor: "var(--line)" }} data-testid="stream-item">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: SOURCE_COLOR[e.source] || SOURCE_COLOR.other }} />
              <span className="flex-1 truncate text-slate-700">{e.summary}</span>
              {e.demo && <span className="chip border-slate-200 text-slate-400 text-[10px]">demo</span>}
              <span className="text-[11px] text-slate-400 whitespace-nowrap">{fmtTime(e.at)}</span>
            </div>))}</div>
        </div>
      </div>
    </div>);
}

function PromoteDialog({ obs, onClose, onPromote, busy, canStanding }) {
  const watchable = !!(obs?.evidence?.filter);
  return (
    <Dialog open={!!obs} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pulse-promote-dialog">
        <DialogHeader>
          <DialogTitle>Turn this observation into action</DialogTitle>
          <DialogDescription>{obs?.text}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5 py-1">
          <button onClick={() => onPromote("decision")} disabled={busy} className="w-full text-left rounded-xl border p-3.5 hover:border-slate-900 transition-colors disabled:opacity-50" style={{ borderColor: "var(--line)" }} data-testid="promote-decision">
            <div className="flex items-center gap-2 font-medium text-sm"><GitBranch className="w-4 h-4 text-slate-500" /> Open a Decision</div>
            <p className="text-xs text-slate-500 mt-1">Adds it to the Decisions inbox for a human to review once. Approving drafts a governed follow-up task.</p>
          </button>
          <button onClick={() => onPromote("standing_order")} disabled={busy || !watchable || !canStanding} className="w-full text-left rounded-xl border p-3.5 hover:border-slate-900 transition-colors disabled:opacity-40 disabled:hover:border-[var(--line)]" style={{ borderColor: "var(--line)" }} data-testid="promote-standing-order">
            <div className="flex items-center gap-2 font-medium text-sm"><Repeat className="w-4 h-4 text-slate-500" /> Create a Standing Order</div>
            <p className="text-xs text-slate-500 mt-1">{!canStanding ? "Requires administration access." : !watchable ? "This observation has no re-checkable metric — use a Decision instead." : "Re-checks this metric on schedule and raises a Decision whenever the pattern persists."}</p>
          </button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} className="rounded-full" data-testid="promote-cancel">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);
}

function WallGrid({ wall }) {
  const map = {}; (wall.cells || []).forEach((c) => { map[`${c.dow}-${c.hour}`] = c; });
  const peak = wall.peak || 1;
  const [hover, setHover] = useState(null);
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[640px]" onMouseLeave={() => setHover(null)}>
        <div className="flex text-[9px] text-slate-400 pl-10 mb-1">{Array.from({ length: 24 }).map((_, h) => <div key={h} className="flex-1 text-center">{h % 3 === 0 ? h : ""}</div>)}</div>
        {DOW.map((d, dow) => (
          <div key={dow} className="flex items-center gap-1 mb-1">
            <div className="w-9 text-[10px] text-slate-500 text-right pr-1">{d}</div>
            {Array.from({ length: 24 }).map((_, h) => {
              const c = map[`${dow}-${h}`]; const v = c?.count || 0; const intensity = v ? 0.15 + 0.85 * (v / peak) : 0;
              return <div key={h} onMouseEnter={() => c && setHover(c)} className="flex-1 aspect-square rounded-sm cursor-pointer transition-transform hover:scale-110" data-testid={`wall-cell-${dow}-${h}`}
                style={{ background: v ? `rgba(15,118,110,${intensity})` : "var(--line)", minWidth: 14 }} title={c ? `${DOW[dow]} ${h}:00 · ${v} events` : ""} />;
            })}
          </div>))}
        {hover && <div className="mt-3 rounded-lg border bg-slate-50 p-2.5 text-xs" style={{ borderColor: "var(--line)" }} data-testid="wall-detail">
          <b>{DOW[hover.dow]} · {hover.hour}:00–{hover.hour + 1}:00</b> — {hover.count} observed activities
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-slate-500">{Object.entries(hover.composition).sort((a, b) => b[1] - a[1]).map(([k, n]) => <span key={k}><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: SOURCE_COLOR[k] || SOURCE_COLOR.other }} />{SOURCE_LABEL[k] || k}: {n}</span>)}</div>
        </div>}
      </div>
    </div>);
}
